import { Injectable } from '@angular/core';
import { WidgetContent } from '@models/widget/widget-content.interface';
import { AcmicService } from '@mapfre-design-library/src/public_api';
import { ActionType } from '@models/widget/action-type.enum';

@Injectable({
  providedIn: 'root'
})
export class WidgetFactoryService {
  isAcmic: boolean;
  isEftEligible: boolean;

  constructor(
    private acmicService: AcmicService
  ) { 
    this.isAcmic = this.acmicService.isACMIC();
  }

  public createWidgetContent(type: ActionType): WidgetContent{
    switch(type){
      case ActionType.AccessMyPolicy:
        return this.createAccessMyPolicyWidget();
      case ActionType.MakePayment:
        return this.createMakePaymentWidget();
      case ActionType.ReportClaim:
        return this.createReportClaimWidget();
      case ActionType.AddDocuments:
        return this.createAddDocsWidget();
      case ActionType.ManageAutopay:
        return this.createManageAutopayWidget();
      case ActionType.ExploreFAQs:
        return this.createExploreFAQs();
      case ActionType.SeeLastInvoice:
        return this.createSeeLastInvoiceWidget();
      case ActionType.ViewTransactions:
        return this.createViewTransactionsWidget();
      case ActionType.DiscoverRewards:
        return this.createDiscoverRewardsWidget();
      case ActionType.GetIdCards:
        return this.createGetIdCardsWidget();
      case ActionType.AskQuestion:
        return this.createAskQuestionWidget();
      case ActionType.BillingRequest:
        return this.createBillingRequestWidget();
      case ActionType.FindRoadsideAid:
        return this.createFindRoadsideAidWidget();
    }
  }

  private createAccessMyPolicyWidget(): WidgetContent{
    return {
      id: "accessMyPolicyId",
      label: "Access my policy",
      position: 1,
      type: ActionType.AccessMyPolicy,
      typeAction: 'policySelector',
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/access-policy-acmic.svg" 
              : "./assets/img/images/widgets/access-policy.svg",
      modalIcon: this.isAcmic 
              ? "./assets/img/images/widgets/modal-access-policy-acmic.svg" 
              : "./assets/img/images/widgets/modal-access-policy.svg",
      modalTitle: "My policy",
      modalSubTitle: "ACCESS_SELECT_POLICY",
      route: ["my-insurance", "", "details"]
    };
   }

   createMakePaymentWidget(): WidgetContent{
     return {
      id: "makePaymentId",
      label: "Make a payment",
      position: 2,
      type: ActionType.MakePayment,
      typeAction: 'redirect',
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/make-payment-acmic.svg"
              : "./assets/img/images/widgets/make-payment.svg",
      route: ["billing-new", "", "make-a-payment"]
    };
   }

   createReportClaimWidget(): WidgetContent{
    return {
      id: "reportClaimId",
      label: "Report a claim",
      position: 3,
      type: ActionType.ReportClaim,
      typeAction: 'policySelector',
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/report-claim-acmic.svg"
              : "./assets/img/images/widgets/report-claim.svg",
      modalIcon: this.isAcmic 
              ? "./assets/img/images/widgets/modal-report-claim-acmic.svg"
              : "./assets/img/images/widgets/modal-report-claim.svg",
      modalTitle: "Report a claim",
      modalSubTitle: "To get started, please select the policy related to your claim.",
      route: ["my-insurance", "reportClaim"]
    };
   }

   createAddDocsWidget(): WidgetContent{
    return {
      id: "addDocsId",
      label: "Add documents",
      position: 4,
      type: ActionType.AddDocuments,
      typeAction: 'modal',
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/add-documents-acmic.svg"
              : "./assets/img/images/widgets/add-documents.svg",
    };
   }

   createManageAutopayWidget(): WidgetContent{
    return {
      id: "managetAutopayId",
      label: "MANAGE_AUTOPAY",
      position: 5,
      type: ActionType.ManageAutopay,
      typeAction: 'redirect',
      route: ["billing", "autopay"],
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/manage-autopay-acmic.svg"
              : "./assets/img/images/widgets/manage-autopay.svg",
    };
   }

   createExploreFAQs(): WidgetContent{
    return {
      id: "exploreFAQsId",
      label: "EXPLORE_FAQS",
      position: 6,
      type: ActionType.ExploreFAQs,
      typeAction: 'redirect',
      route: ["faq"],
      icon: "./assets/img/images/widgets/explore-faqs.svg",
    };
   }

   private createSeeLastInvoiceWidget(): WidgetContent{
    return {
      id: "seeLastInvoiceId",
      label: "SEE_LAST_INVOICE",
      position: 7,
      type: ActionType.SeeLastInvoice,
      typeAction: 'redirect',
      route: ["billing-new", "", "documents"],
      fragment: 'lastInvoice',
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/last-invoice-acmic.svg"
              : "./assets/img/images/widgets/last-invoice.svg",
    };
   }

   private createViewTransactionsWidget(): WidgetContent{
    return {
      id: "viewTransactionsId",
      label: "VIEW_TRANSACTIONS",
      position: 8,
      type: ActionType.ViewTransactions,
      typeAction: 'redirect',
      route: ["billing-new", "", "details"],
      fragment: 'transHistory',
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/view-transactions-acmic.svg"
              : "./assets/img/images/widgets/view-transactions.svg",
    };
   }

   private createDiscoverRewardsWidget(): WidgetContent{
    return {
      id: "discoverRewardsId",
      label: "DISCOVER_REWARDS",
      position: 9,
      type: ActionType.DiscoverRewards,
      typeAction: 'redirect',
      route: ["https://www.mapfreinsurance.com/my-mapfre-rewards/"],
      icon: "./assets/img/images/widgets/discover-rewards.svg",
    };
   }

   private createGetIdCardsWidget(): WidgetContent{
    return {
      id: "getIdCardsId",
      label: "GET_ID_CARDS",
      position: 10,
      type: ActionType.GetIdCards,
      typeAction: 'redirect',
      route: ["id-cards"],
      icon: "./assets/img/images/widgets/id-cards-acmic.svg",
    };
   }

   private createAskQuestionWidget(): WidgetContent{
    return {
      id: "askQuestionId",
      label: "ASK_QUESTION",
      position: 11,
      type: ActionType.AskQuestion,
      typeAction: 'modal',
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/ask-question-acmic.svg"
              : "./assets/img/images/widgets/ask-question.svg",
    };
   }

   private createBillingRequestWidget(): WidgetContent{
    return {
      id: "billingRequestId",
      label: "BILLING_REQUEST",
      position: 12,
      type: ActionType.BillingRequest,
      typeAction: 'policySelector',
      icon: "./assets/img/images/widgets/billing-request.svg",
      modalIcon: "./assets/img/images/widgets/modal-billing-request-black.svg",
      modalTitle: "BILLING_REQUEST",
      modalSubTitle: "INFO_SELECT_POLICY",
      route: ["my-insurance", "", "billing-request"]
    };
   }

   private createFindRoadsideAidWidget(): WidgetContent{
    return {
      id: "findRoadsideAidId",
      label: "FIND_ROADSIDE_AID",
      position: 13,
      type: ActionType.FindRoadsideAid,
      typeAction: 'redirect',
      route: this.isAcmic 
                ? ["https://wa.aaa.com/roadside/ers?cmpid=int_dr_mapfre_ixx-2025-acmic-cust-portal&med=dr_mapfre_int&src=ins_eng&cont=acmic-i_cust-portal_request-ers"]
                : ["https://www.mapfreinsurance.com/blog/car-break-down-tips/"],
      icon: this.isAcmic 
              ? "./assets/img/images/widgets/find-roadside-aid-acmic.svg"
              : "./assets/img/images/widgets/find-roadside-aid.svg",
    };
  }
}
